// useProviderDraft — the shared "draft into the IM's Outlook mailbox" action
// behind <EmailDraftButton>. It POSTs a recipient-optional draft to the
// `/api/outlook/draft` endpoint, then toasts and opens the returned mailbox deep
// link for the IM to review + send.
//
// This is the one canonical way the app turns a "Draft email" affordance into a
// real Outlook draft — no `mailto:` links. One hook instance owns one in-flight
// draft (`busy`), so render a separate instance per independent draft affordance
// (e.g. one per table row).

import { useState } from 'react';
import { apiFetch } from '@/lib/api';
import { useToast } from '@/lib/useToast';

export interface ProviderDraftInput {
    /**
     * Optional recipient. Omit for a recipient-less draft the IM addresses in
     * their own mailbox — client email addresses are often not on file, so the
     * draft endpoint accepts a blank `To:`.
     */
    to?: string | null;
    subject: string;
    body: string;
    /**
     * Optional files for the draft. Outlook shows each one on the draft. Omit
     * the field for a draft with no file: the request then carries the same
     * body as before, so no existing caller changes behaviour.
     */
    attachments?: ProviderDraftAttachment[];
}

/** One file that the draft carries. The server caps the total size at 3 MB. */
export interface ProviderDraftAttachment {
    /** File name that the mailbox shows, for example `titan-holdings.xlsx`. */
    name: string;
    /** MIME type of the file. */
    contentType: string;
    /** The file bytes, in base64. */
    contentBytes: string;
}

export function useProviderDraft() {
    const { pushToast } = useToast();
    const [busy, setBusy] = useState(false);

    const draft = async (input: ProviderDraftInput): Promise<void> => {
        setBusy(true);
        try {
            const res = await apiFetch('/api/outlook/draft', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    to: input.to ?? '',
                    subject: input.subject,
                    body: input.body,
                    ...(input.attachments && input.attachments.length > 0
                        ? { attachments: input.attachments }
                        : {}),
                }),
            });
            const data = (await res.json().catch(() => ({}))) as {
                ok?: boolean;
                webUrl?: string;
                error?: string;
                message?: string;
            };
            if (!res.ok || !data.ok) {
                throw new Error(data.error ?? data.message ?? `Draft failed (${res.status})`);
            }
            pushToast({ kind: 'success', message: 'Draft created in Outlook — opening it now' });
            if (data.webUrl) window.open(data.webUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            pushToast({
                kind: 'error',
                message: err instanceof Error ? err.message : 'Could not create the draft',
            });
        } finally {
            setBusy(false);
        }
    };

    return { draft, busy };
}
